import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  type EmailLogEntry,
  type EmailStats,
  getEmailStats,
  getSuperadminApi,
  listEmails,
} from "@praesto/core";
import { Toolbar, Input, Button } from "@praesto/ui-kit";

/**
 * Delivery log for every email the platform sends.
 *
 * Two things about this screen are deliberate. The search demands a reason
 * because it is audited server-side (and mirrored to the customer when scoped
 * to one tenant), and only ONE filter applies at a time — the API refuses
 * combinations that would need a composite index nobody created.
 */
const STATUS_LABEL: Record<string, string> = {
  accepted: "Presa in carico",
  delivered: "Consegnata",
  bounced: "Rimbalzata",
  complained: "Segnalata come spam",
  rejected: "Rifiutata",
  delayed: "In ritardo",
  failed: "Mai partita",
};

const KIND_LABEL: Record<string, string> = {
  "user.invite": "Invito utente",
  "superadmin.ownerRelink": "Ricollegamento titolare",
  "superadmin.operatorInvite": "Invito operatore",
  "superadmin.tenantCreated": "Invito a struttura nuova",
  "hub.code": "Codice di accesso",
  "signup.verify": "Verifica registrazione",
  "signup.reset": "Reimposta password",
  "lead.alert": "Nuovo contatto",
  "notification.email": "Notifica",
};

function formatDate(value: unknown): string {
  const seconds =
    typeof value === "object" && value !== null && "_seconds" in value
      ? (value as { _seconds: number })._seconds
      : null;
  if (seconds === null) return "—";
  return new Date(seconds * 1000).toLocaleString("it-IT");
}

export function EmailsTab() {
  // Filters live in the URL, not in component state: that is what makes
  // "look at this bounce" something you can paste into a chat, keeps the back
  // button meaningful, and lets other screens link straight into a filtered
  // view (see the per-tenant link in TenantsTable).
  const search = useSearch({ strict: false }) as {
    to?: string;
    tenantId?: string;
    status?: string;
    reason?: string;
  };
  const navigate = useNavigate();

  const [stats, setStats] = useState<EmailStats | null>(null);
  const [entries, setEntries] = useState<ReadonlyArray<EmailLogEntry>>([]);
  const [to, setTo] = useState(search.to ?? "");
  const [reason, setReason] = useState(search.reason ?? "");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Opening the tab shows the most recent mail straight away. Without this the
  // screen only answers the "I already know the address" case, and the other
  // two it exists for — spotting trouble before a customer reports it, and
  // checking what actually went out — need you to guess a query first.
  // The listing is audited like any search, with an automatic reason.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getSuperadminApi();
      const token = await api.getAuthToken();
      const [s, r] = await Promise.all([
        getEmailStats(api.apiBase, token),
        listEmails(
          api.apiBase,
          {
            ...(search.to !== undefined ? { to: search.to } : {}),
            ...(search.tenantId !== undefined ? { tenantId: search.tenantId } : {}),
            ...(search.status !== undefined ? { status: search.status } : {}),
            reason: search.reason ?? "consultazione elenco",
          },
          token,
        ),
      ]);
      if (cancelled) return;
      setStats(s);
      setEntries(r.entries);
      setSearched(true);
    })().catch((e) => {
      if (!cancelled) setError(String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [search.to, search.tenantId, search.status, search.reason]);

  const applySearch = (): void => {
    // A targeted search for one person's mail needs a stated purpose. The
    // unfiltered listing above does not: it is browsing, not looking someone up.
    if (to.trim() !== "" && reason.trim() === "") {
      setError("Indica il motivo della ricerca: viene registrato nell'audit.");
      return;
    }
    setError(null);
    // Navigate rather than fetch: the URL is the source of truth, and the
    // effect above reacts to it. One code path for typing a search, following
    // a link, pressing back, or reloading.
    void navigate({
      to: ".",
      search: {
        ...(to.trim() !== "" ? { to: to.trim() } : {}),
        ...(search.tenantId !== undefined ? { tenantId: search.tenantId } : {}),
        ...(search.status !== undefined ? { status: search.status } : {}),
        ...(reason.trim() !== "" ? { reason: reason.trim() } : {}),
      },
    } as never);
  };

  const deliveryRate =
    stats && stats.total > 0 ? Math.round((stats.delivered / stats.total) * 100) : null;

  return (
    <div>
      <Toolbar title="Email di sistema" />

      {stats && (
        <p className="text-sm text-fg-muted mb-3">
          {/* Counts and rates are reading units: mono, and they never wrap. */}
          Ultimi 30 giorni: <strong className="font-mono whitespace-nowrap">
            {stats.total}
          </strong>{" "}
          inviate
          {deliveryRate !== null && (
            <>
              {" "}
              · <span className="font-mono whitespace-nowrap">{deliveryRate}%</span> consegnate
            </>
          )}{" "}
          · <span className="font-mono whitespace-nowrap">{stats.bounced}</span> rimbalzate
          {stats.suppressed > 0 && (
            <>
              {" "}
              · <span className="font-mono whitespace-nowrap">{stats.suppressed}</span> verso
              indirizzi bloccati da SES
            </>
          )}
        </p>
      )}

      {stats && stats.problemTenants.length > 0 && (
        <p className="text-sm text-danger mb-3">
          Strutture con indirizzi non recapitabili: {stats.problemTenants.join(", ")}
        </p>
      )}

      <div className="flex gap-2 mb-3">
        <Input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Indirizzo esatto (es. mario@hotel.it)"
        />
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Motivo della ricerca"
        />
        <Button onClick={applySearch}>Cerca</Button>
      </div>

      {error && (
        <p className="text-sm text-danger mb-3" role="alert">
          {error}
        </p>
      )}

      {searched && entries.length === 0 && (
        <p className="text-sm text-fg-muted">
          {to.trim() === ""
            ? "Nessuna email registrata."
            : "Nessuna email trovata per questo indirizzo."}
        </p>
      )}

      <ul className="space-y-1 text-sm">
        {entries.map((e) => (
          <li key={e.id} className="border-b border-border-default py-2">
            <button
              type="button"
              className="text-left w-full"
              onClick={() => setExpanded(expanded === e.id ? null : e.id)}
            >
              <strong>{STATUS_LABEL[e.status] ?? e.status}</strong>
              {/* Shown alongside the status, never instead of it: a suppressed
                  address surfaces as a bounce OR as a complaint, and telling
                  the two apart is the whole point of this screen. */}
              {e.suppressed && <span className="text-danger"> · indirizzo bloccato da SES</span>} ·{" "}
              {e.to} · <span className="text-fg-muted">{KIND_LABEL[e.kind] ?? e.kind}</span> ·{" "}
              <span className="font-mono whitespace-nowrap">{formatDate(e.sentAt)}</span>
              <br />
              <span className="text-fg-muted">{e.subject}</span>
            </button>
            {expanded === e.id && (
              <div className="mt-2 pl-3 border-l border-border-default text-mono-xs">
                {e.statusDetail && <p className="mb-1">{e.statusDetail}</p>}
                {(e.events ?? []).map((ev, i) => (
                  <p key={i} className="text-fg-muted">
                    {ev.type} · <span className="whitespace-nowrap">{formatDate(ev.at)}</span>
                    {ev.detail && ` · ${ev.detail}`}
                  </p>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
